import { useEffect, useRef } from "react";
import { useFormikContext, type FormikErrors } from "formik";

interface OnInvalidSubmitProps<Values> {
  /** Called once per submit that fails validation, with that submit's errors. */
  onInvalid: (errors: FormikErrors<Values>) => void;
}

/**
 * Runs `onInvalid` after a submit fails validation — exactly once per submit.
 * Render it inside a Formik <Form>.
 *
 * Don't read `errors` in the submit button's onClick instead: Formik validates
 * *after* the click, so on the first submit of an untouched form `errors` is
 * still empty there and nothing happens; only the second click sees them.
 *
 * Formik bumps submitCount and sets isSubmitting together, then validates;
 * isSubmitting drops back to false only once that submit's errors are final,
 * so that's when this fires.
 */
function OnInvalidSubmit<Values>({ onInvalid }: OnInvalidSubmitProps<Values>) {
  const { submitCount, isSubmitting, errors } = useFormikContext<Values>();
  const handledSubmitCount = useRef(0);
  const latestOnInvalid = useRef(onInvalid);
  latestOnInvalid.current = onInvalid;

  useEffect(() => {
    if (isSubmitting || submitCount === handledSubmitCount.current) return;
    handledSubmitCount.current = submitCount;
    if (Object.keys(errors).length > 0) latestOnInvalid.current(errors);
  }, [submitCount, isSubmitting, errors]);

  return null;
}

export default OnInvalidSubmit;
